import { SetStateAction, useEffect, useState } from "react";
import { authClient } from "../utils/amplifyClient";
import {
    Box,
    Button,
    Checkbox,
    Container,
    FileUpload,
    FormField,
    Header,
    Input,
    Modal,
    Select,
    Spinner,
    Table,
    Textarea,
} from './legacy-widgets';
import ChatBubbleOutlined from '@mui/icons-material/ChatBubbleOutlineOutlined';
import * as Sentry from "@sentry/browser";

import { uploadData, getUrl, remove, getProperties } from "aws-amplify/storage";
import { v4 as uuidv4 } from 'uuid';

import {
    S3_RESOURCE_LIBRARY_PATH,
} from "../utils/constants";

import { ResourceLibraryTab } from "../types/resourceLibraryTypes";
import { FormTemplatesTab } from "./resource-library/FormTemplatesTab";
import { SafetyPlanTemplatesTab } from "./resource-library/SafetyPlanTemplatesTab";
import { HelpSupportDocsTab } from "./resource-library/HelpSupportDocsTab";

import '../css/FormsContent.css';
import '../css/ResourceLibraryContent.css';

import { createFormTemplate as createFormTemplateMutation, createResourceLibrary as createResourceLibraryMutation, deleteFormTemplate as deleteFormTemplateMutation, updateFormTemplateGroup as updateFormTemplateGroupMutation } from "../graphql/mutations";
import {
    listDocuments as listDocumentsQuery,
    listFormTemplateGroups as listFormTemplateGroupsQuery, listFormTemplates as listFormTemplatesQuery, listForms as listFormsQuery, listResourceLibraries as listResourceLibrariesQuery, listVideos as listVideosQuery
} from "../graphql/queries";
import { CompanyOwner, Form, FormTemplate, FormTemplateGroup, Document as DocumentType, Video } from "../types/schema";
import { useUserContext } from "../contexts/UserContext";
import { useFormsContext } from "../contexts/FormsContext";
import { PageHeader, Stack, IconForm, IconShield, IconInfo } from "./ui";

const createDocumentCustom = /* GraphQL */ `
  mutation CreateDocument($input: CreateDocumentInput!) {
    createDocument(input: $input) {
      id
      title
      contentPath
      resourceLibraryID
      sharedWithCompanyIds
      folderName
      safetyLesson
      createdAt
      updatedAt
    }
  }
`;

const createVideoCustom = /* GraphQL */ `
  mutation CreateVideo($input: CreateVideoInput!) {
    createVideo(input: $input) {
      id
      title
      contentPath
      resourceLibraryID
      sharedWithCompanyIds
      folderName
      safetyLesson
      createdAt
      updatedAt
    }
  }
`;

const createFormTemplateGroupCustom = /* GraphQL */ `
  mutation CreateFormTemplateGroup($input: CreateFormTemplateGroupInput!) {
    createFormTemplateGroup(input: $input) {
      id
      name
      description
      resourceLibraryID
      sharedWithCompanyIds
      createdAt
      updatedAt
    }
  }
`;

const updateDocumentCustomMutation = /* GraphQL */ `
  mutation UpdateDocument($input: UpdateDocumentInput!) {
    updateDocument(input: $input) {
      id
      title
      contentPath
      folderName
      sharedWithCompanyIds
      resourceLibraryID
      createdAt
      updatedAt
      safetyLesson
    }
  }
`;

const updateVideoCustomMutation = /* GraphQL */ `
  mutation UpdateVideo($input: UpdateVideoInput!) {
    updateVideo(input: $input) {
      id
      title
      contentPath
      folderName
      sharedWithCompanyIds
      resourceLibraryID
      createdAt
      updatedAt
      safetyLesson
    }
  }
`;

const deleteDocumentCustomMutation = /* GraphQL */ `
  mutation DeleteDocument($input: DeleteDocumentInput!) {
    deleteDocument(input: $input) {
      id
      title
      contentPath
      resourceLibraryID
      createdAt
      updatedAt
      owner
    }
  }
`;

const deleteVideoCustomMutation = /* GraphQL */ `
  mutation DeleteVideo($input: DeleteVideoInput!) {
    deleteVideo(input: $input) {
      id
      title
      contentPath
      resourceLibraryID
      createdAt
      updatedAt
      owner
    }
  }
`;

type TableItem = {
    id: string;
    name: string;
    type: 'folder' | 'document' | 'video';
    children?: TableItem[];
    // Document/Video-specific properties
    contentPath?: string;
    uploadDate?: string;
    sharedWithCompanyIds?: (string | null)[] | null | undefined;
    originalDoc?: DocumentType;
    originalVid?: Video;
    videoUrl?: string;
};

// Site Admin resource library — TotalEquipIQ Resource Library page shell
// Zero Cloudscape imports — all custom React + CSS with .zrl- prefix
const TAB_INFO_BANNERS: Record<ResourceLibraryTab, string> = {
    'form-templates': 'Content here is maintained by TotalEquipIQ and pushed to all customers. Templates define structure only, not content. Customers customize after activation.',
    'safety-plans': 'Content here is maintained by TotalEquipIQ and pushed to customers. When a customer activates a Safety Plan, they receive a full editable copy — topics, sequence, and all.',
    'help-docs': 'Published docs appear in every customer\'s Resources → Support tab. Module-gated docs are only visible to customers with that module active.',
};

export const SiteAdminResourceLibrary = () => {
    const [activeTab, setActiveTab] = useState<ResourceLibraryTab>('form-templates');

    return (
        <div className="page-container zrl-container">
            <PageHeader
                title="TotalEquipIQ Resource Library"
                subtitle="Platform-level content maintained by TotalEquipIQ. Customers access these when activating modules."
            />

            <div className="zrl-tabs" role="tablist" aria-label="Resource library">
                <button
                    role="tab"
                    id="zrl-tab-form-templates"
                    aria-selected={activeTab === 'form-templates'}
                    aria-controls="zrl-panel-form-templates"
                    className={`zrl-tab ${activeTab === 'form-templates' ? 'active' : ''}`}
                    onClick={() => setActiveTab('form-templates')}
                >
                    <span className="zrl-tab-icon"><IconForm /></span> Form Templates
                </button>
                <button
                    role="tab"
                    id="zrl-tab-safety-plans"
                    aria-selected={activeTab === 'safety-plans'}
                    aria-controls="zrl-panel-safety-plans"
                    className={`zrl-tab ${activeTab === 'safety-plans' ? 'active' : ''}`}
                    onClick={() => setActiveTab('safety-plans')}
                >
                    <span className="zrl-tab-icon"><IconShield /></span> Safety Plan Templates
                </button>
                <button
                    role="tab"
                    id="zrl-tab-help-docs"
                    aria-selected={activeTab === 'help-docs'}
                    aria-controls="zrl-panel-help-docs"
                    className={`zrl-tab ${activeTab === 'help-docs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('help-docs')}
                >
                    <span className="zrl-tab-icon"><ChatBubbleOutlined /></span> Help &amp; Support Docs
                </button>
            </div>

            <div className="zrl-info-banner" role="status">
                <span className="zrl-info-banner-icon"><IconInfo /></span>
                <span>{TAB_INFO_BANNERS[activeTab]}</span>
            </div>

            <div
                className="zrl-section-container"
                role="tabpanel"
                id={`zrl-panel-${activeTab}`}
                aria-labelledby={`zrl-tab-${activeTab}`}
            >
                {activeTab === 'form-templates' && <FormTemplatesTab />}
                {activeTab === 'safety-plans' && <SafetyPlanTemplatesTab />}
                {activeTab === 'help-docs' && <HelpSupportDocsTab />}
            </div>
        </div>
    );
}

// single company is for management of resources within the single company -- companyOwners can manage, companyUsers can view
export const SingleCompanyResourceLibrary = () => {

    const [resourceLibraryId, setResourceLibraryId] = useState<string>('')

    const [documentFileData, setDocumentFileData] = useState<File[]>([]);
    const [documentsList, setDocumentsList] = useState<DocumentType[]>([])
    const [existingDocumentFolders, setExistingDocumentFolders] = useState<string[]>([]);
    const [documentToDelete, setDocumentToDelete] = useState<string | null>(null)
    const [documentToEdit, setDocumentToEdit] = useState<DocumentType | null>(null)
    const [newDocumentModal, setNewDocumentModal] = useState(false)
    const [expandedDocuments, setExpandedDocuments] = useState<TableItem[]>([]);
    const [tableDocuments, setTableDocuments] = useState<TableItem[]>([]);
    const [viewDocumentModal, setViewDocumentModal] = useState<string | null>(null)

    const [videoFileData, setVideoFileData] = useState<File[]>([]);
    const [videosList, setVideosList] = useState<Video[]>([])
    const [existingVideoFolders, setExistingVideoFolders] = useState<string[]>([]);
    const [videoToDelete, setVideoToDelete] = useState<string | null>(null);
    const [videoToEdit, setVideoToEdit] = useState<Video | null>(null);
    const [newVideoModal, setNewVideoModal] = useState(false)
    const [expandedVideos, setExpandedVideos] = useState<TableItem[]>([]);
    const [tableVideos, setTableVideos] = useState<TableItem[]>([]);
    const [viewVideoModal, setViewVideoModal] = useState<string | null>(null)

    const [formTemplateGroups, setFormTemplateGroups] = useState<FormTemplateGroup[] | null>(null)
    const [openNewTemplate, setOpenNewTemplate] = useState<boolean>(false)
    const [formName, setFormName] = useState('')
    const [formDescription, setFormDescription] = useState("")
    const [allFormData, setAllFormData] = useState<Form[]>([])
    const [formsList, setFormsList] = useState<FormTemplate[]>([])
    const [selectedTemplateGroup, setSelectedTemplateGroup] = useState<FormTemplateGroup | null>(null)
    const [showRemoveTemplateModal, setShowRemoveTemplateModal] = useState(false)
    const [selectedInspectionForms, setSelectedInspectionForms] = useState<Form[]>([])
    const [selectedDailyForms, setSelectedDailyForms] = useState<Form[]>([])
    const [templateGroupToRemove, setTemplateGroupToRemove] = useState<FormTemplateGroup | null>(null)
    const [showTemplateListModal, setShowTemplateListModal] = useState(false)
    const [showTemplateAssignModal, setShowTemplateAssignModal] = useState(false)

    const [selectedItemFolder, setSelectedItemFolder] = useState<TableItem | null>(null)
    const [selectedFolder, setSelectedFolder] = useState<{ label: string; value: string } | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSafetyAssignModal, setShowSafetyAssignModal] = useState(false)
    const [newFolderName, setNewFolderName] = useState<string | null>('')
    const [isSafetyLesson, setIsSafetyLesson] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const { companyUser, company, companyOwner } = useUserContext()
    const { refreshForms } = useFormsContext();

    const companyId = (company?.id as string) || companyOwner?.companyId;

    const [loading, setLoading] = useState(false);

    const fetchResourceLibrary = async () => {
        if (!companyOwner?.id && !companyUser && !company) return;
        setLoading(true)
        try {
            if (!resourceLibraryId) {
                const resourceLibraryResponse = await authClient.graphql({
                    query: listResourceLibrariesQuery,
                })

                if (resourceLibraryResponse.data.listResourceLibraries.items.length > 0) {
                    // Check if there's a resource library for current company owner
                    const currentResourceLibrary = resourceLibraryResponse.data.listResourceLibraries.items.find(
                        item => item.companyOwnerId === companyOwner?.id || company?.companyOwnerId
                    );

                    if (currentResourceLibrary) {
                        setResourceLibraryId(currentResourceLibrary.id);
                        await getFormTemplateGroups()
                        await getPDFs();
                        await getVideos();
                    } else {
                        // Create new resource library for current company owner
                        const newResourceLibraryEntry = await authClient.graphql({
                            query: createResourceLibraryMutation,
                            variables: {
                                input: {
                                    companyOwnerId: companyOwner?.id || company?.companyOwnerId as string,
                                }
                            }
                        });
                        const resourceLibrary = newResourceLibraryEntry.data.createResourceLibrary;
                        setResourceLibraryId(resourceLibrary.id);
                        await getFormTemplateGroups()
                        await getPDFs();
                        await getVideos();
                    }
                } else {
                    // No resource libraries exist, create first one for current company
                    const newResourceLibraryEntry = await authClient.graphql({
                        query: createResourceLibraryMutation,
                        variables: {
                            input: {
                                companyOwnerId: companyOwner?.id || company?.companyOwnerId as string,
                            }
                        }
                    });
                    const resourceLibrary = newResourceLibraryEntry.data.createResourceLibrary;
                    setResourceLibraryId(resourceLibrary.id);
                    await getFormTemplateGroups()
                    await getPDFs();
                    await getVideos();
                }
            } else {
                await getFormTemplateGroups()
                await getPDFs();
                await getVideos();
            }
        } catch (error) {
            console.error('Error fetching resource library:', error);
            Sentry.captureException(error);
        } finally {
            setLoading(false)
        }
    }

    const getAllForms = async (companyOwner: CompanyOwner) => {
        if (!companyOwner) return;
        setLoading(true)
        try {

            const formResponse = await authClient.graphql({
                query: listFormsQuery,
            });
            const allFormsData = formResponse?.data?.listForms?.items
            const filteredForms = allFormsData.filter((form => form.companyId === companyId))

            setAllFormData(filteredForms)

        } catch (error) {
            console.error("error :>> ", error);
            Sentry.captureException(error);
        } finally {
            setLoading(false)
        }
    }

    const getPDFs = async () => {
        if (!company || !companyId) return;

        setLoading(true)
        try {
            const response = await authClient.graphql({
                query: listDocumentsQuery,
            });

            const filteredDocuments = response.data.listDocuments.items.filter(doc => doc.sharedWithCompanyIds?.includes(companyId || company?.id))

            console.log("response documents", response.data.listDocuments.items)

            const uniqueFolders = [...new Set(filteredDocuments
                .map(doc => doc.folderName)
                .filter(folderName => folderName !== null && folderName !== undefined)
            )];

            setExistingDocumentFolders(uniqueFolders.filter(Boolean) as string[]);

            setDocumentsList(filteredDocuments)

        } catch (error) {
            console.error('Error fetching PDF files:', error);
            Sentry.captureException(error);
        } finally {
            setLoading(false)
        }
    };

    const organizeDocumentsByFolder = (documents: DocumentType[], folders: string[]): TableItem[] => {
        // Make sure we have an "Uncategorized" folder for documents without a folder
        const allFolders = [...new Set([...folders, 'Uncategorized'])];

        // Create the folder items first
        const result: TableItem[] = allFolders.map(folder => ({
            id: folder,
            name: folder,
            type: 'folder',
            children: []
        }));

        // Add documents to their respective folders
        documents.forEach(doc => {

            const folderName = doc.folderName || 'Uncategorized';
            const folderItem = result.find(item => item.name === folderName);

            if (folderItem) {
                folderItem.children?.push({
                    id: doc.id,
                    name: doc.title,
                    contentPath: doc.contentPath,
                    uploadDate: doc.updatedAt || doc.createdAt,
                    type: 'document',
                    originalDoc: doc
                });
            }
        });

        // Remove empty folders
        return result
            .map(folder => ({
                ...folder,
                children: folder.children?.sort((a, b) => a.name.localeCompare(b.name))
            }))
            .filter(folder => folder.children && folder.children.length > 0);
    };

    // Then use this function to transform your data
    useEffect(() => {
        if (documentsList && existingDocumentFolders) {
            console.log('documents list', documentsList)
            try {
                const organizedData = organizeDocumentsByFolder(documentsList, existingDocumentFolders);
                setTableDocuments(organizedData);
                console.log("organized data", organizedData)
            } catch (error) {
                console.error('Error organizing documents:', error);
                Sentry.captureException(error);
            }
        }
    }, [documentsList, existingDocumentFolders]);

    const getVideos = async () => {
        if (!companyId || !company) return;
        setLoading(true)
        try {
            const response = await authClient.graphql({
                query: listVideosQuery,
            });

            const filteredVideos = response.data.listVideos.items.filter(vid => vid.sharedWithCompanyIds?.includes(companyId || company?.id))

            const uniqueFolders = [...new Set(filteredVideos
                .map(doc => doc.folderName)
                .filter(folderName => folderName !== null && folderName !== undefined)
            )];
            setExistingVideoFolders(uniqueFolders.filter(Boolean) as string[]);

            setVideosList(filteredVideos)

        } catch (error) {
            console.error('Error fetching video files:', error);
            Sentry.captureException(error);
        } finally {
            setLoading(false)
        }
    };

    const organizeVideosByFolder = async (videos: Video[], folders: string[]): Promise<TableItem[]> => {
        // Make sure we have an "Uncategorized" folder for documents without a folder
        const allFolders = [...new Set([...folders, 'Uncategorized'])];

        // Create the folder items first
        const result: TableItem[] = allFolders.map(folder => ({
            id: folder,
            name: folder,
            type: 'folder',
            children: []
        }));

        // Add videos to their respective folders
        await Promise.all(videos.map(async (vid) => {
            const folderName = vid.folderName || 'Uncategorized';
            const folderItem = result.find(item => item.name === folderName);
            const vidUrl = await getUrl({ path: vid.contentPath });

            if (folderItem) {
                folderItem.children?.push({
                    id: vid.id,
                    name: vid.title,
                    contentPath: vid.contentPath,
                    uploadDate: vid.updatedAt || vid.createdAt,
                    type: 'video',
                    originalVid: vid,
                    videoUrl: vidUrl.url.href
                });
            }
        }));
        return result
            .map(folder => ({
                ...folder,
                children: folder.children?.sort((a, b) => a.name.localeCompare(b.name))
            }))
            .filter(folder => folder.children && folder.children.length > 0);
    }

    // Remove empty folders after all videos are processed
    // Then use this function to transform your data
    useEffect(() => {
        const loadVideoData = async () => {
            if (videosList && existingVideoFolders) {
                try {
                    const organizedData = await organizeVideosByFolder(videosList, existingVideoFolders);
                    setTableVideos(organizedData);
                } catch (error) {
                    console.error('Error organizing videos:', error);
                    Sentry.captureException(error);
                }
            }
        };

        loadVideoData();
    }, [videosList, existingVideoFolders]);

    const getFormTemplateGroups = async () => {
        setLoading(true)
        try {
            const templateResponse = await authClient.graphql({
                query: listFormTemplateGroupsQuery
            })
            const allTemplates = templateResponse.data.listFormTemplateGroups.items
            const filteredTemplates = allTemplates.filter((template) => {
                return template.sharedWithCompanyIds?.includes(companyId ?? '');
            });

            if (filteredTemplates) {
                setFormTemplateGroups(filteredTemplates as FormTemplateGroup[])

            }
        } catch (error) {
            console.error('Error fetching form templates:', error);
            Sentry.captureException(error);
        } finally {
            setLoading(false)
        }
    }

    const handlePdfUpload = async (folderName?: string | null) => {
        if (!documentFileData || !companyOwner) return;

        try {
            setLoading(true);

            const file = documentFileData[0];
            // const folderName = selectedFolder?.value || null;

            const documentsInFolder = documentsList.filter(doc => doc.folderName === folderName);

            // Get sharedCompanyIds from other documents in folder
            let existingSharedCompanyIds: string[] = [];
            if (documentsInFolder.length >= 1) {
                existingSharedCompanyIds = documentsInFolder[0].sharedWithCompanyIds?.filter((x): x is string => x != null) ?? []
            } else {
                existingSharedCompanyIds = [companyId as string]; // <-- add this
            }

            // Create a unique file name
            const fileName = `${uuidv4()}-DOCUMENT-${file.name}`;
            const filePath = S3_RESOURCE_LIBRARY_PATH.documentPath(companyOwner.id, fileName);

            // Upload to S3
            const uploadPromise = await uploadData({
                path: filePath,
                data: file
            })

            const result = await uploadPromise.result

            if (result) {
                try {
                    const headResult = await getProperties({
                        path: filePath
                    });

                    if (headResult.size !== file.size) {
                        throw new Error('File size mismatch - upload may be corrupted');
                    }

                    if (headResult && headResult.size && headResult.size > 0 && resourceLibraryId) {

                        await authClient.graphql({
                            query: createDocumentCustom,
                            variables: {
                                input: {
                                    title: file.name,
                                    contentPath: filePath,
                                    resourceLibraryID: resourceLibraryId,
                                    folderName: folderName,
                                    sharedWithCompanyIds: existingSharedCompanyIds,
                                    safetyLesson: isSafetyLesson
                                }
                            }
                        });
                    }
                } catch (error) {
                    console.error('Object does not exist in S3:', error);
                    alert('Error uploading document. Please try again.');
                    setVideoFileData([]);
                    setSelectedFolder(null);
                    setIsSafetyLesson(false);
                    setNewVideoModal(false)
                    setNewFolderName(null)
                    setLoading(false);
                }

            }

        } catch (error) {
            console.error('Error uploading file:', error);
        } finally {
            setDocumentFileData([]);
            setSelectedFolder(null);
            setIsSafetyLesson(false);
            setNewDocumentModal(false)
            setNewFolderName(null)
            await getPDFs()
            setLoading(false);
        }
    };

    const handleVideoUpload = async (folderName?: string | null) => {
        if (!videoFileData || videoFileData.length === 0 || !companyOwner) return;
        try {
            setLoading(true);

            const file = videoFileData[0];
            // const folderName = selectedFolder?.value || null;

            // Get all videos in the folder
            const vidsInFolder = videosList.filter(vid => vid.folderName === folderName);

            // Get sharedCompanyIds from other documents in folder
            let existingSharedCompanyIds: string[] = [];
            if (vidsInFolder.length >= 1) {

                existingSharedCompanyIds = vidsInFolder[0].sharedWithCompanyIds?.filter((x): x is string => x != null) ?? []
            } else {
                existingSharedCompanyIds = [companyId as string]; // <-- add this
            }

            // Create a unique file name
            const fileName = `${uuidv4()}-VIDEO-${file.name}`;
            const filePath = S3_RESOURCE_LIBRARY_PATH.videoPath(companyOwner.id, fileName)

            // Upload to S3
            const uploadPromise = await uploadData({
                path: filePath,
                data: file
            });

            const result = await uploadPromise.result

            if (result) {
                try {
                    const headResult = await getProperties({
                        path: filePath
                    });

                    if (headResult.size !== file.size) {
                        throw new Error('File size mismatch - upload may be corrupted');
                    }

                    if (headResult && headResult.size && headResult.size > 0 && resourceLibraryId) {

                        await authClient.graphql({
                            query: createVideoCustom,
                            variables: {
                                input: {
                                    title: file.name,
                                    contentPath: filePath,
                                    resourceLibraryID: resourceLibraryId,
                                    folderName: folderName,
                                    sharedWithCompanyIds: existingSharedCompanyIds,
                                    safetyLesson: isSafetyLesson
                                }
                            }
                        });

                    }
                } catch (error) {
                    console.error('Object does not exist in S3:', error);
                    alert('Error uploading video. Please try again.');
                    setVideoFileData([]);
                    setSelectedFolder(null);
                    setIsSafetyLesson(false);
                    setNewVideoModal(false)
                    setNewFolderName(null)
                    setLoading(false);
                }

            }

        } catch (error) {
            console.error('Error uploading video:', error);
        } finally {
            setVideoFileData([]);
            setSelectedFolder(null);
            setIsSafetyLesson(false);
            setNewVideoModal(false)
            setNewFolderName(null)
            await getVideos();
            setLoading(false);
        }
    };

    const handleNewTemplateGroup = async () => {
        if (!formName && !formDescription) return;

        setLoading(true)
        try {

            await authClient.graphql({
                query: createFormTemplateGroupCustom,
                variables: {
                    input: {
                        name: formName,
                        description: formDescription,
                        resourceLibraryID: resourceLibraryId,
                        sharedWithCompanyIds: [companyId]
                    }
                }
            });

        } catch (error) {
            console.error('Error creating form template:', error);
            Sentry.captureException(error);
        } finally {
            setOpenNewTemplate(false)
            setFormName('')
            setFormDescription('')
            getFormTemplateGroups()
            setLoading(false)
        }
    }

    const handleFormSelection = async () => {
        if (!selectedTemplateGroup || !companyOwner) return;
        setLoading(true)
        try {
            // Get current form templates for this group
            const existingTemplatesResponse = await authClient.graphql({
                query: listFormTemplatesQuery,
                variables: {
                    filter: {
                        formTemplateGroupID: { eq: selectedTemplateGroup.id }
                    }
                }
            });

            const existingTemplates = existingTemplatesResponse.data.listFormTemplates.items;
            const existingInpsection = existingTemplates.filter(template => !template.dailyServiceLogForm)
            const existingDaily = existingTemplates.filter(template => template.dailyServiceLogForm)

            // Find templates to delete (templates that exist but aren't in selectedForms)
            const templatesToDelete = [...existingInpsection, ...existingDaily].filter(template =>
                (!selectedInspectionForms.some(form => form.name === template.templateName) &&
                    !selectedDailyForms.some(form => form.name === template.templateName)) &&
                template.originalCompanyId === companyId
            );

            // Find forms to add (forms in selectedForms that don't have a template yet)
            const inspectionFormsToAdd = selectedInspectionForms.filter(form =>
                !existingTemplates.some(template => template.templateName === form.name)
            );

            const dailyFormsToAdd = selectedDailyForms.filter(form =>
                !existingTemplates.some(template => template.templateName === form.name)
            );

            // Delete templates that are no longer selected
            for (const template of templatesToDelete) {
                await authClient.graphql({
                    query: deleteFormTemplateMutation,
                    variables: {
                        input: {
                            id: template.id
                        }
                    }
                });
            }

            // Create templates for newly selected forms
            for (const form of inspectionFormsToAdd) {
                await authClient.graphql({
                    query: createFormTemplateMutation,
                    variables: {
                        input: {
                            templateName: form.name,
                            templateData: form.formData,
                            createdByCompanyOwnerId: companyOwner.id,
                            originalCompanyId: companyId,
                            formTemplateGroupID: selectedTemplateGroup.id,
                            dailyServiceLogForm: false
                        }
                    }
                });
            }

            for (const form of dailyFormsToAdd) {
                await authClient.graphql({
                    query: createFormTemplateMutation,
                    variables: {
                        input: {
                            templateName: form.name,
                            templateData: form.formData,
                            createdByCompanyOwnerId: companyOwner.id,
                            originalCompanyId: companyId,
                            formTemplateGroupID: selectedTemplateGroup.id,
                            dailyServiceLogForm: true
                        }
                    }
                });
            }

            // Reset selected state and refresh data
            getFormTemplateGroups();
            refreshForms();

        } catch (error) {
            console.error('Error assigning companies:', error);
            Sentry.captureException(error);
        } finally {
            setSelectedInspectionForms([]);
            setSelectedDailyForms([]);
            setSelectedTemplateGroup(null);
            setLoading(false)
        }
    }

    const handleOpenViewFormTemplatesInGroupModal = async (item: SetStateAction<FormTemplateGroup | null>) => {
        setSelectedTemplateGroup(item)
        setShowTemplateListModal(true);
        setLoading(true)

        if (item && typeof item !== 'function' && item.id) {
            const templateGroupId = item.id

            const allFormTemplates = await authClient.graphql({
                query: listFormTemplatesQuery,
            });
            const formTemplates = allFormTemplates.data.listFormTemplates.items;
            const filteredForms = formTemplates.filter((form) => form.formTemplateGroupID === templateGroupId);

            setFormsList(filteredForms as FormTemplate[]);
        } else {
            setFormsList([]);
        }
        setLoading(false)

    }

    const handleDeleteVideo = async (path: string) => {
        setLoading(true)
        try {

            // Delete from database
            const response = await authClient.graphql({
                query: listVideosQuery
            });

            const video = response.data.listVideos.items.find(v => v.contentPath === path);

            if (video) {
                await authClient.graphql({
                    query: deleteVideoCustomMutation,
                    variables: {
                        input: {
                            id: video.id
                        }
                    }
                });

                // Delete from S3
                await remove({
                    path: path
                });
                const updatedVideoList = videosList.filter(vid => vid.contentPath !== path);
                setVideosList(updatedVideoList);
            }
        } catch (error) {
            console.error('Error deleting video:', error);
            Sentry.captureException(error);
        } finally {
            setLoading(false)
        }
    }

    const handleDeleteDocument = async (path: string) => {
        setLoading(true)
        try {

            // Delete from database
            const response = await authClient.graphql({
                query: listDocumentsQuery
            });

            const document = response.data.listDocuments.items.find(v => v.contentPath === path);

            if (document) {
                await authClient.graphql({
                    query: deleteDocumentCustomMutation,
                    variables: {
                        input: {
                            id: document.id
                        }
                    }
                });
                // Delete from S3
                await remove({
                    path: path
                });
                // Update state by removing deleted document
                const updatedDocumentList = documentsList.filter(doc => doc.contentPath !== path);
                setDocumentsList(updatedDocumentList);
            }
        } catch (error) {
            console.error('Error deleting video:', error);
            Sentry.captureException(error);
        } finally {
            setLoading(false)
        }
    }

    const handleRemoveTemplateGroup = async (selectedTemplateGroup: FormTemplateGroup) => {
        if (!selectedTemplateGroup) return
        setLoading(true)

        setShowRemoveTemplateModal(true)

        const updatedCompanyIds = selectedTemplateGroup.sharedWithCompanyIds?.filter(id =>
            id !== companyId
        ) || [];

        try {
            await authClient.graphql({
                query: updateFormTemplateGroupMutation,
                variables: {
                    input: {
                        id: selectedTemplateGroup.id,
                        sharedWithCompanyIds: updatedCompanyIds
                    }
                }
            })

        } catch (error) {
            console.error('Error removing template group:', error);
            Sentry.captureException(error);
        } finally {
            setShowRemoveTemplateModal(false)
            setTemplateGroupToRemove(null)
            getFormTemplateGroups()
            setLoading(false)
        }
    }

    const handleEditDocument = async (document: DocumentType, folderName?: string | null) => {

        const activeFolderName = folderName ? folderName : document.folderName

        // Get all documents in the folder
        const documentsInFolder = documentsList.filter(doc => doc.folderName === activeFolderName);

        // Get sharedCompanyIds from other documents in folder
        let existingSharedCompanyIds: string[] = [];
        if (documentsInFolder.length >= 1) {
            // Get documents except the one being edited
            const otherDocs = documentsInFolder.filter(doc => doc.id !== document.id);

            // Get shared company IDs from first other document found
            if (otherDocs.length > 0 && otherDocs[0].sharedWithCompanyIds) {
                existingSharedCompanyIds = otherDocs[0].sharedWithCompanyIds.filter(
                    (id): id is string => id !== null
                );
            }
            if (existingSharedCompanyIds.length === 0) {
                existingSharedCompanyIds = [companyId as string]; // <-- add this
            }
        }
        try {
            await authClient.graphql({
                query: updateDocumentCustomMutation,
                variables: {
                    input: {
                        id: document.id,
                        folderName: folderName !== null ? folderName : document.folderName,
                        sharedWithCompanyIds: existingSharedCompanyIds,
                        safetyLesson: isSafetyLesson ? !document.safetyLesson : document.safetyLesson
                    }
                }
            });

        } catch (error) {
            console.error('Error editing document:', error);
            Sentry.captureException(error);
        } finally {
            setDocumentToEdit(null)
            setShowEditModal(false)
            setIsSafetyLesson(false)
            setLoading(false)
            await getPDFs()
        }
    }

    const handleEditVideo = async (video: Video, folderName?: string | null) => {
        // Get all documents in the folder
        const activeFolderName = folderName !== null ? folderName : video.folderName

        const videosInFolder = videosList.filter(vid => vid.folderName === activeFolderName);

        // Get sharedCompanyIds from other documents in folder
        let existingSharedCompanyIds: string[] = [];
        if (videosInFolder.length >= 1) {
            // Get documents except the one being edited
            const otherVids = videosInFolder.filter(vid => vid.id !== video.id);

            // Get shared company IDs from first other document found
            if (otherVids.length > 0 && otherVids[0].sharedWithCompanyIds) {
                existingSharedCompanyIds = otherVids[0].sharedWithCompanyIds.filter(
                    (id): id is string => id !== null
                );
            }
            if (existingSharedCompanyIds.length === 0) {
                existingSharedCompanyIds = [companyId as string]; // <-- add this
            }
        }

        try {
            await authClient.graphql({
                query: updateVideoCustomMutation,
                variables: {
                    input: {
                        id: video.id,
                        folderName: folderName !== null ? folderName : video.folderName,
                        sharedWithCompanyIds: existingSharedCompanyIds,
                        safetyLesson: isSafetyLesson !== undefined ? isSafetyLesson : video.safetyLesson,
                    }
                }
            });

        } catch (error) {
            console.error('Error editing document:', error);
            Sentry.captureException(error);
        } finally {
            setDocumentToEdit(null)
            setVideoToEdit(null)
            setShowEditModal(false)
            setIsSafetyLesson(false)
            setLoading(false)
            await getVideos()
        }
    }

    const handleOpenTemplateAssignModal = async (item: SetStateAction<FormTemplateGroup | null>) => {
        setSelectedTemplateGroup(item);
        setLoading(true)

        if (item && typeof item !== 'function' && item.id) {
            const grantedFormTemplateGroupId = item.id;

            const allFormTemplates = await authClient.graphql({
                query: listFormTemplatesQuery,
            });
            const formTemplates = allFormTemplates.data.listFormTemplates.items;
            const filteredFormTemplates = formTemplates.filter((template) => template.formTemplateGroupID === grantedFormTemplateGroupId);

            const matchingForms = allFormData.filter(form =>
                filteredFormTemplates.some(template => template.templateName === form.name)
            );

            setSelectedInspectionForms(matchingForms.filter(form => !form.dailyServiceLogForm))
            setSelectedDailyForms(matchingForms.filter(form => form.dailyServiceLogForm));
        } else {
            setSelectedInspectionForms([]);
            setSelectedDailyForms([]);
        }
        setShowTemplateAssignModal(true)
        setLoading(false)
    }

    const handleOpenAssignSafetyStatusModal = async (item: SetStateAction<FormTemplateGroup | null> | TableItem) => {
        setLoading(true);

        // Reset both selected items first
        setSelectedItemFolder(null);

        // Handle document/video folder case
        if (item && 'type' in item && item.type === 'folder') {
            setSelectedItemFolder(item as TableItem);
        }

        // if (companyIds) {
        //     const allCompanies = await authClient.graphql({
        //         query: listCompaniesQuery
        //     });
        //     const companies = allCompanies.data.listCompanies.items;
        //     const filteredCompanies = companies.filter((company) => companyIds?.includes(company.id));

        //     setSelectedCompanies(filteredCompanies as Company[]);
        // } else {
        //     setSelectedCompanies([]);
        // }

        setShowSafetyAssignModal(true);
        setLoading(false);
    }

    const handleSafetyContentAssignment = async () => {

        if (!selectedItemFolder) return;
        setLoading(true)

        try {

            if (selectedItemFolder !== null && selectedItemFolder.children && selectedItemFolder.children.length > 0) {
                if (selectedItemFolder.children[0].type === "document") {
                    const documents = selectedItemFolder.children

                    // Use Promise.all to wait for all document updates to complete
                    await Promise.all(documents.map(async (document: TableItem) => {
                        await authClient.graphql({
                            query: updateDocumentCustomMutation,
                            variables: {
                                input: {
                                    id: document.id,
                                    safetyLesson: document.originalDoc?.safetyLesson
                                }
                            }
                        });
                    }));
                    getPDFs()
                }

                if (selectedItemFolder.children[0].type === "video") {
                    const videos = selectedItemFolder.children

                    // Use Promise.all to wait for all document updates to complete
                    await Promise.all(videos.map(async (video: TableItem) => {
                        await authClient.graphql({
                            query: updateVideoCustomMutation,
                            variables: {
                                input: {
                                    id: video.id,
                                    safetyLesson: video.originalVid?.safetyLesson
                                }
                            }
                        });
                    }));
                    getVideos()
                }

                setSelectedItemFolder(null);
            }

            setShowSafetyAssignModal(false);

        } catch (error) {
            console.error('Error assigning companies:', error);
            Sentry.captureException(error);
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (companyOwner || companyUser) {
            getAllForms(companyOwner as CompanyOwner)
            fetchResourceLibrary()
        }
    }, [companyUser, companyOwner]);

    return (
        <>
            <Stack size="xl">
                <Header variant="h1">Resource Library Content</Header>

                <Table
                    expandableRows={{
                        getItemChildren: (item: TableItem) => item.children || [],
                        isItemExpandable: (item: TableItem) => item.type === 'folder' && Array.isArray(item.children) && item.children.length > 0,
                        expandedItems: expandedDocuments,
                        onExpandableItemToggle: ({ detail }) => {
                            const item = detail.item;
                            const isExpanded = detail.expanded;

                            setExpandedDocuments(prev => {
                                if (isExpanded) {
                                    return [...prev, item];
                                } else {
                                    return prev.filter(expandedItem => expandedItem.id !== item.id);
                                }
                            });
                        }
                    }}
                    columnDefinitions={[
                        {
                            id: "name",
                            header: "Name",
                            cell: item => (
                                <div style={{
                                    maxWidth: '200px',
                                    wordWrap: 'break-word',
                                    whiteSpace: 'normal'
                                }}>
                                    {item.name}
                                </div>
                            ),
                            width: 300,
                            isRowHeader: true
                        },
                        {
                            id: "uploadDate",
                            header: "Upload Date",
                            cell: item => item.type === 'document' && item.uploadDate ?
                                new Date(item.uploadDate).toLocaleDateString() :
                                ""
                        },
                        {
                            id: "actions",
                            header: "Actions",
                            cell: item => item.type === 'document' ? (
                                <Stack direction="horizontal" size="xs">
                                    <Button
                                        variant="icon"
                                        iconName="search"
                                        ariaLabel="View"
                                        onClick={async () => {
                                            // Get fresh signed URL before opening
                                            const freshUrl = await getUrl({
                                                path: item.contentPath ?? ''
                                            });

                                            const UrlToView = item.contentPath?.toLowerCase().endsWith('.pdf')
                                                ? `https://docs.google.com/viewer?url=${encodeURIComponent(freshUrl.url.href)}&embedded=true`
                                                : freshUrl.url.href;

                                            setViewDocumentModal(UrlToView);
                                        }}
                                    />

                                    <Button
                                        variant="icon"
                                        iconName="download"
                                        ariaLabel="Download"
                                        onClick={async () => {
                                            // Get fresh signed URL before opening
                                            const freshUrl = await getUrl({
                                                path: item.contentPath ?? ''
                                            });
                                            window.open(freshUrl.url.href, '_blank', 'noopener,noreferrer');
                                        }}
                                    />

                                    {companyOwner && <Button
                                        variant="icon"
                                        iconName="edit"
                                        ariaLabel="Edit"
                                        onClick={() => {
                                            // Find the original document from documentList
                                            const originalDoc = documentsList.find(doc => doc.id === item.id);
                                            if (originalDoc) {
                                                setDocumentToEdit(originalDoc);
                                                setShowEditModal(true);
                                            }
                                        }}
                                    />
                                    }
                                    {companyOwner && <Button
                                        variant="icon"
                                        iconName="remove"
                                        ariaLabel="Delete"
                                        onClick={() => {
                                            setShowDeleteModal(true)
                                            setDocumentToDelete(item.contentPath || null)
                                        }}
                                    >
                                        Delete
                                    </Button>}
                                </Stack>
                            ) : (
                                companyOwner ? (
                                    <Button
                                        iconName="edit"
                                        variant="link"
                                        onClick={() => {
                                            handleOpenAssignSafetyStatusModal(item)
                                        }}
                                        ariaLabel="Bulk edit docs available for daily popup safety"
                                    />

                                ) : <></>
                            )
                        }
                    ]}
                    resizableColumns
                    items={tableDocuments}
                    stripedRows
                    loadingText="Loading Documents"
                    trackBy="id"
                    empty={
                        < Box textAlign="center" color="inherit" >
                            <b>No Documents found</b>
                            <Box padding={{ bottom: "s" }}>
                                Upload Documents to see them listed here
                            </Box>
                        </Box>
                    }
                    header={
                        <Header
                            counter={`(${documentsList.length})`}
                            actions={companyOwner ?
                                <Button onClick={() => setNewDocumentModal(true)}>
                                    Add New Document
                                </Button>
                                : undefined
                            }
                        >
                            Documents
                        </Header>
                    }
                />

                <Table
                    expandableRows={{
                        getItemChildren: (item: TableItem) => item.children || [],
                        isItemExpandable: (item: TableItem) => item.type === 'folder' && Array.isArray(item.children) && item.children.length > 0,
                        expandedItems: expandedVideos,
                        onExpandableItemToggle: ({ detail }) => {
                            const item = detail.item;
                            const isExpanded = detail.expanded;

                            setExpandedVideos(prev => {
                                if (isExpanded) {
                                    return [...prev, item];
                                } else {
                                    return prev.filter(expandedItem => expandedItem.id !== item.id);
                                }
                            });
                        }
                    }}
                    items={tableVideos}
                    stripedRows
                    columnDefinitions={[
                        {
                            id: "name",
                            header: "Name",
                            cell: item => (
                                <div style={{
                                    maxWidth: '200px',
                                    wordWrap: 'break-word',
                                    whiteSpace: 'normal'
                                }}>
                                    {item.name}
                                </div>
                            ),
                            width: 300,
                            isRowHeader: true
                        },
                        {
                            id: "preview",
                            header: "Preview",
                            cell: item => item.type === 'video' ? (
                                <video width="160" height="90" controls>
                                    <source src={item.videoUrl} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            ) : (<></>)
                        },
                        {
                            id: "uploadDate",
                            header: "Upload Date",
                            cell: item => item.type === 'video' && item.uploadDate ?
                                new Date(item.uploadDate).toLocaleDateString() :
                                ""
                        },
                        {
                            id: "actions",
                            header: "Actions",
                            cell: item => item.type === 'video' ? (
                                <Stack direction="horizontal" size="xs">
                                    <Button
                                        variant="icon"
                                        iconName="search"
                                        ariaLabel="View"
                                        onClick={async () => {
                                            // Get fresh signed URL before opening
                                            const freshUrl = await getUrl({
                                                path: item.contentPath ?? ''
                                            });

                                            setViewVideoModal(freshUrl.url.href)
                                        }}

                                    />
                                    <Button
                                        variant="icon"
                                        iconName="download"
                                        ariaLabel="Download"
                                        onClick={async () => {
                                            // Get fresh signed URL before opening
                                            const freshUrl = await getUrl({
                                                path: item.contentPath ?? ''
                                            });
                                            window.open(freshUrl.url.href, '_blank', 'noopener,noreferrer');
                                        }}

                                    />
                                    {companyOwner && <Button
                                        variant="icon"
                                        iconName="edit"
                                        ariaLabel="Edit"
                                        onClick={() => {
                                            // Find the original document from documentList
                                            const originalVid = videosList.find(vid => vid.id === item.id);
                                            if (originalVid) {
                                                setVideoToEdit(originalVid);
                                                setShowEditModal(true);
                                            }
                                        }}
                                    />
                                    }
                                    {companyOwner && <Button
                                        variant="icon"
                                        iconName="remove"
                                        ariaLabel="Delete"
                                        onClick={() => {
                                            setShowDeleteModal(true)
                                            setVideoToDelete(item.contentPath || null)
                                        }
                                        }
                                    />
                                    }
                                </Stack>
                            ) : (
                                companyOwner ? (
                                    <Button
                                        iconName="edit"
                                        variant="link"
                                        onClick={() => {
                                            handleOpenAssignSafetyStatusModal(item)
                                        }}
                                        ariaLabel="Bulk edit vids available for daily popup safety"
                                    />

                                ) : <></>
                            )

                        }
                    ]}
                    resizableColumns
                    loadingText="Loading videos..."
                    trackBy="id"
                    loading={loading}
                    empty={
                        <Box textAlign="center" color="inherit">
                            <b>No videos found</b>

                            <Box padding={{ bottom: "s" }}>
                                Upload videos to see them listed here
                            </Box>

                        </Box>
                    }
                    header={
                        <>
                            <Header
                                counter={`(${videosList.length})`}
                                actions={companyOwner ?
                                    <Button
                                        onClick={() => setNewVideoModal(true)}>Add New Video</Button> : undefined

                                }
                            >
                                Video Files
                            </Header>
                        </>
                    }
                />

                {companyOwner && <Table
                    items={formTemplateGroups || []}
                    columnDefinitions={[
                        {
                            id: "name",
                            header: "Template Name",
                            cell: item => item.name
                        },
                        {
                            id: "description",
                            header: "Description",
                            cell: item => item.description
                        },
                        {
                            id: "createdAt",
                            header: "Created Date",
                            cell: item => new Date(item.createdAt).toLocaleDateString()
                        },
                        {
                            id: "viewTemplates",
                            header: "Templates Assigned to Group",
                            cell: item => (
                                <Button
                                    iconName="external"
                                    variant="icon"
                                    onClick={() => {
                                        handleOpenViewFormTemplatesInGroupModal(item)
                                    }}
                                    ariaLabel="Open Modal to view templates assigned to group"
                                />
                            )
                        },
                        {
                            id: "assignForms",
                            header: `Assign Forms from ${company?.name} to the Template Group`,
                            cell: item => (
                                <Button
                                    iconName="edit"
                                    variant="icon"
                                    onClick={() => {
                                        handleOpenTemplateAssignModal(item)

                                    }}
                                    ariaLabel="Assign forms"
                                />
                            )
                        },
                        {
                            id: "remove",
                            header: "Remove",
                            cell: item => (
                                <Button
                                    iconName="close"
                                    variant="icon"
                                    onClick={() => {
                                        setShowRemoveTemplateModal(true)
                                        setTemplateGroupToRemove(item)

                                    }}
                                    ariaLabel="Unassign template group from company"
                                />
                            )
                        }
                    ]}
                    loadingText="Loading templates..."
                    stripedRows
                    loading={loading}
                    trackBy="id"
                    empty={
                        <Box textAlign="center" color="inherit">
                            <b>No form templates found</b>
                            <Box padding={{ bottom: "s" }}>
                                Create a new template group to get started
                            </Box>
                        </Box>
                    }
                    header={
                        <Header
                            counter={`(${formTemplateGroups?.length || 0})`}
                            actions={
                                <Button onClick={() => setOpenNewTemplate(true)}>Create New Template Group</Button>
                            }
                        >
                            Form Template Groups
                        </Header>
                    }
                />}
            </Stack>

            {/* modal for super admin to assign templates */}
            < Modal
                visible={showTemplateAssignModal && selectedTemplateGroup !== null}
                onDismiss={() => {
                    setShowTemplateAssignModal(false)
                    setSelectedTemplateGroup(null)
                }
                } header={`Assign Forms to ${selectedTemplateGroup?.name}`}>
                {
                    loading ? (<Spinner size="normal" />) : (

                        <Stack size="m">
                            <FormField label="All Inspection Forms:">
                                <Stack size="s">
                                    {allFormData?.filter(form => !form.dailyServiceLogForm).map((form) => (
                                        <Checkbox
                                            key={form.id}
                                            checked={
                                                selectedInspectionForms.some(selectedInspectionForm => selectedInspectionForm.id === form.id)
                                            }
                                            onChange={({ detail }) => {
                                                if (detail.checked) {
                                                    setSelectedInspectionForms([...(selectedInspectionForms || []), form]);
                                                } else {
                                                    setSelectedInspectionForms((selectedInspectionForms || []).filter(selectedInspectionForm => selectedInspectionForm.id !== form.id));
                                                }
                                            }}
                                        >
                                            {form.name}
                                        </Checkbox>))
                                    }
                                </Stack>
                            </FormField >
                            <FormField label="All Daily Service Forms:">
                                <Stack size="s">
                                    {allFormData?.filter(form => form.dailyServiceLogForm)
                                        .map((form) => (
                                            <Checkbox
                                                key={form.id}
                                                checked={
                                                    selectedDailyForms.some(selectedDailyForm => selectedDailyForm.id === form.id)
                                                }
                                                onChange={({ detail }) => {
                                                    if (detail.checked) {
                                                        setSelectedDailyForms([...(selectedDailyForms || []), form]);
                                                    } else {
                                                        setSelectedDailyForms((selectedDailyForms || []).filter(selectedDailyForm => selectedDailyForm.id !== form.id));
                                                    }
                                                }}
                                            >
                                                {form.name}
                                            </Checkbox>))}
                                </Stack>
                            </FormField>
                            <Button variant="primary" onClick={() => {
                                handleFormSelection()
                            }}>
                                Submit
                            </Button>
                        </Stack>
                    )}
            </Modal >

            {/* modal for viewing a pdf or video on the screen */}
            < Modal
                visible={(viewDocumentModal || viewVideoModal) !== null}
                onDismiss={() => {
                    setViewDocumentModal(null)
                    setViewVideoModal(null)

                }}
                size="large"
                header={viewDocumentModal ? "View Document" : "View Video"} >
                {viewDocumentModal && (
                    <>
                        {viewDocumentModal.toLowerCase().startsWith('https://docs.google.com/viewer') ? (
                            <iframe
                                src={viewDocumentModal}
                                width="100%"
                                height="500px"
                                style={{
                                    border: "none",
                                }}
                            />
                        ) : (
                            <img
                                src={viewDocumentModal}
                                alt="Document"
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "500px",
                                    objectFit: "contain"
                                }}
                            />
                        )}
                        <div style={{ marginTop: "10px", textAlign: "center" }}>
                            <a href={viewDocumentModal} target="_blank" rel="noopener noreferrer">
                                Download to View if it doesn't load
                            </a>
                        </div>
                    </>
                )}
                {
                    viewVideoModal && (
                        <video
                            src={viewVideoModal}
                            controls
                            width="100%"
                            height="auto"
                            style={{ maxHeight: "500px" }}
                        />
                    )
                }
            </Modal >

            {/* modal to add a new document or video */}
            < Modal
                visible={newDocumentModal || newVideoModal}
                onDismiss={() => {
                    setNewDocumentModal(false)
                    setNewVideoModal(false)
                }}
                header={newDocumentModal ? "Add New Document" : "Add New Video"}
            >
                <Stack size="m">
                    <FormField label={newDocumentModal ? "Document" : "Video"}>
                        <FileUpload
                            onChange={({ detail }) => {
                                if (newDocumentModal) {
                                    setDocumentFileData(detail.value);
                                } else {
                                    setVideoFileData(detail.value);
                                }
                            }}
                            value={(newDocumentModal ? documentFileData : videoFileData) as readonly File[]}
                            i18nStrings={{
                                uploadButtonText: (e) => (e ? "Choose files" : "Choose file"),
                                dropzoneText: (e) => e ? "Drop files to upload" : "Drop file to upload",
                                removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                                limitShowFewer: "Show fewer files",
                                limitShowMore: "Show more files",
                                errorIconAriaLabel: "Error",
                            }}
                            constraintText={newDocumentModal
                                ? "File must be a PDF, PNG, JPEG, JPG"
                                : "File must be MP4, WebM, MOV, or OGG format (MP4 recommended for best compatibility)"
                            }
                            accept={newDocumentModal ? ".pdf,.png,.jpg,.JPEG,.jpeg" : ".mp4,.webm,.ogg,.mov"}
                            multiple={false}
                            showFileLastModified
                            showFileSize
                        />
                    </FormField>
                    <FormField label={`Add ${newDocumentModal ? "Document" : "Video"} To Folder`}>
                        <Select
                            selectedOption={selectedFolder}
                            onChange={({ detail }) => setSelectedFolder({ label: detail.selectedOption.label ?? '', value: detail.selectedOption.value ?? '' })}
                            options={[
                                { label: "None", value: "" },
                                ...(newDocumentModal ? existingDocumentFolders : existingVideoFolders).map(folder => ({
                                    label: folder,
                                    value: folder
                                })),
                                { label: "Create new folder", value: "CREATE_NEW" }
                            ]}
                            placeholder="Select a folder"
                            expandToViewport
                        />
                    </FormField>
                    {selectedFolder?.value === "CREATE_NEW" && (
                        <FormField label="New folder name">
                            <Input
                                value={newFolderName || ""}
                                onChange={({ detail }) => setNewFolderName(detail.value)}
                                placeholder="Enter new folder name"
                            />
                        </FormField>
                    )}

                    <FormField label="Safety Lesson">
                        <Checkbox
                            checked={isSafetyLesson}
                            onChange={({ detail }) => setIsSafetyLesson(detail.checked)}
                        >
                            Check to make visible for scheduling safety content
                        </Checkbox>
                    </FormField>

                    <Button
                        onClick={() => {
                            const folderName = selectedFolder?.value === "CREATE_NEW"
                                ? newFolderName
                                : selectedFolder?.value;

                            newDocumentModal ? handlePdfUpload(folderName) : handleVideoUpload(folderName);
                        }}
                        disabled={(newDocumentModal ? !documentFileData : !videoFileData) ||
                            (newDocumentModal ? documentFileData.length === 0 : videoFileData.length === 0) ||
                            loading}
                    >
                        {loading ? <Spinner /> : `Upload ${newDocumentModal ? "Document" : "Video"}`}
                    </Button>

                </Stack>
            </Modal >

            {/* new template modal */}
            < Modal
                visible={openNewTemplate}
                onDismiss={() => setOpenNewTemplate(false)}
                header="Create New Template Group"
            >
                <Stack size="m">
                    <FormField label="Template Group Name">
                        <Input
                            value={formName}
                            onChange={({ detail }) => setFormName(detail.value)}
                            placeholder="Enter template group name"
                        />
                    </FormField>

                    <FormField label="Description">
                        <Textarea
                            value={formDescription}
                            onChange={({ detail }) => setFormDescription(detail.value)}
                            placeholder="Enter description"
                        />
                    </FormField>

                    <Button variant="primary" onClick={() => {
                        handleNewTemplateGroup()
                    }}>
                        Create Template Group
                    </Button>
                </Stack>
            </Modal >

            {/* manage safety lesson status modal */}
            <Modal
                visible={showSafetyAssignModal && selectedItemFolder !== null}
                onDismiss={() => {
                    setSelectedItemFolder(null)
                    setShowSafetyAssignModal(false)
                }}
                header={<>Manage Safety Lesson Status for <span style={{ textDecoration: "underline" }}>{selectedItemFolder?.name}</span> Folder</>}
            >
                {loading ? <Spinner size="normal" /> : (
                    <Stack size="m">
                        <FormField label={`Items in ${selectedItemFolder?.name} folder:`}>
                            <Stack size="s">
                                {/* Set all items toggle */}
                                <Container>
                                    <Stack direction="horizontal" size="s">
                                        <Checkbox
                                            checked={selectedItemFolder?.children?.every(item =>
                                                item.type === 'document' ? item.originalDoc?.safetyLesson : item.originalVid?.safetyLesson
                                            ) || false}
                                            indeterminate={
                                                selectedItemFolder?.children?.some(item =>
                                                    item.type === 'document' ? item.originalDoc?.safetyLesson : item.originalVid?.safetyLesson
                                                ) &&
                                                !selectedItemFolder?.children?.every(item =>
                                                    item.type === 'document' ? item.originalDoc?.safetyLesson : item.originalVid?.safetyLesson
                                                )
                                            }
                                            onChange={({ detail }) => {
                                                if (!selectedItemFolder) return;
                                                const updatedItems = selectedItemFolder?.children?.map((item): TableItem => {
                                                    if (item.type === 'document') {
                                                        return {
                                                            ...item,
                                                            originalDoc: item.originalDoc
                                                                ? { ...item.originalDoc, safetyLesson: detail.checked }
                                                                : item.originalDoc
                                                        };
                                                    } else {
                                                        return {
                                                            ...item,
                                                            originalVid: item.originalVid
                                                                ? { ...item.originalVid, safetyLesson: detail.checked }
                                                                : item.originalVid
                                                        };
                                                    }
                                                });
                                                setSelectedItemFolder({
                                                    ...selectedItemFolder,
                                                    children: updatedItems
                                                });
                                            }}
                                        >
                                            <strong>Set all items as Safety Lessons</strong>
                                        </Checkbox>
                                    </Stack>
                                </Container>

                                {/* Individual item toggles */}
                                {selectedItemFolder?.children?.map((item) => (
                                    <Checkbox
                                        key={item.id}
                                        checked={(item.type === 'document' ? item.originalDoc?.safetyLesson : item.originalVid?.safetyLesson) || false}
                                        onChange={({ detail }) => {
                                            if (!selectedItemFolder) return;
                                            const updatedItems = selectedItemFolder?.children?.map((i): TableItem => {
                                                if (i.id === item.id) {
                                                    if (i.type === 'document') {
                                                        return {
                                                            ...i,
                                                            originalDoc: i.originalDoc
                                                                ? { ...i.originalDoc, safetyLesson: detail.checked }
                                                                : i.originalDoc
                                                        };
                                                    } else {
                                                        return {
                                                            ...i,
                                                            originalVid: i.originalVid
                                                                ? { ...i.originalVid, safetyLesson: detail.checked }
                                                                : i.originalVid
                                                        };
                                                    }
                                                }
                                                return i;
                                            });
                                            setSelectedItemFolder({
                                                ...selectedItemFolder,
                                                children: updatedItems
                                            });
                                        }}

                                    >
                                        {item.type === 'document' ? item.originalDoc?.title : item.originalVid?.title} ({item.type === 'document' ? 'Document' : 'Video'})
                                    </Checkbox>
                                ))}
                            </Stack>
                        </FormField>

                        <Stack direction="horizontal" size="xs">
                            <Button onClick={() => {
                                setSelectedItemFolder(null)
                                setShowSafetyAssignModal(false)
                            }}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={() => {
                                handleSafetyContentAssignment()
                            }}>
                                Update Safety Lesson Status
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </Modal>

            {/* confirmation modal for deleting video/pdf */}
            <Modal
                visible={showDeleteModal}
                onDismiss={() => {
                    setShowDeleteModal(false);
                    setVideoToDelete(null);
                    setDocumentToDelete(null)
                }}
                header="Confirmation of Deletion"
            >
                <Stack size="m">
                    <Box>Are you sure you want to delete this {videoToDelete ? 'video' : 'document'}?</Box>
                    <Stack direction="horizontal" size="xs">
                        <Button
                            variant="primary"
                            onClick={() => {
                                if (videoToDelete) {
                                    handleDeleteVideo(videoToDelete);
                                }
                                if (documentToDelete) {
                                    handleDeleteDocument(documentToDelete)
                                }
                                setShowDeleteModal(false);
                                setVideoToDelete(null);
                                setDocumentToDelete(null)
                            }}
                        >
                            Delete
                        </Button>
                        <Button
                            onClick={() => {
                                setShowDeleteModal(false);
                                setVideoToDelete(null);
                                setDocumentToDelete(null)
                            }}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Stack>
            </Modal>

            {/* modal to show which templates are in each template group */}
            <Modal
                visible={showTemplateListModal && selectedTemplateGroup !== null}
                onDismiss={() => {
                    setShowTemplateListModal(false);
                    setSelectedTemplateGroup(null)
                    setFormsList([])
                }}
                header={`Templates in ${selectedTemplateGroup?.name || ''}`}
            >
                {loading ? <Spinner size="normal" /> : (
                    <Stack size="m">
                        <Table
                            items={formsList || []}
                            stripedRows
                            trackBy="id"
                            columnDefinitions={[
                                {
                                    id: "name",
                                    header: "Template Name",
                                    cell: item => item.templateName
                                },
                                {
                                    id: "dailyService",
                                    header: "Type of Form",
                                    cell: item => item.dailyServiceLogForm ? "Daily Service" : "Inspection"
                                },
                                {
                                    id: "createdAt",
                                    header: "Created",
                                    cell: item => new Date(item.createdAt).toLocaleDateString()
                                }
                            ]}
                            empty={
                                <Box textAlign="center" color="inherit">
                                    <b>No templates found</b>
                                    <Box padding={{ bottom: "s" }}>
                                        This template group has no templates yet
                                    </Box>
                                </Box>
                            }
                        />
                        <Button onClick={() => {
                            setSelectedTemplateGroup(null);
                            setShowTemplateListModal(false)
                            setFormsList([])
                        }}>
                            Close
                        </Button>
                    </Stack>
                )}
            </Modal>

            {/* confirmation modal for removing template group */}
            <Modal
                visible={showRemoveTemplateModal}
                onDismiss={() => {
                    setShowRemoveTemplateModal(false);
                    setTemplateGroupToRemove(null);
                }}
                header='Confirmation of Removal'
            >
                <Stack size="m">
                    <Box>Action will remove templates from this group from your company forms</Box>
                    <Box variant="small">*Note* this will not delete template group or templates from other company owners</Box>
                    <Stack direction="horizontal" size="xs">
                        <Button
                            onClick={() => {
                                setShowRemoveTemplateModal(false);
                                setTemplateGroupToRemove(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => {

                                handleRemoveTemplateGroup(templateGroupToRemove as FormTemplateGroup);

                                setShowRemoveTemplateModal(false);
                            }}
                        >
                            Delete
                        </Button>
                    </Stack>
                </Stack>
            </Modal>

            {/* confirmation modal for editing video/pdf to add folder */}
            <Modal
                visible={showEditModal}
                onDismiss={() => {
                    setShowEditModal(false);
                    setVideoToEdit(null);
                    setDocumentToEdit(null)
                    setIsSafetyLesson(false)
                }}
                header={`Edit ${documentToEdit ? "Document" : "Video"}`}
            >
                <Stack size="m">
                    <FormField label="Safety Lesson">
                        <Box>Currently {(videoToEdit || documentToEdit)?.safetyLesson ? "Visible" : "Not Visible"} for Scheduling Safety Content.</Box>                        <Checkbox
                            checked={isSafetyLesson}
                            onChange={({ detail }) => setIsSafetyLesson(detail.checked)}
                        >
                            check to change
                        </Checkbox>
                    </FormField>
                    <FormField label="Folder (Update or Remove)">
                        <Select
                            selectedOption={selectedFolder || (videoToEdit?.folderName ? { label: videoToEdit.folderName, value: videoToEdit.folderName } : null) || (documentToEdit?.folderName ? { label: documentToEdit.folderName, value: documentToEdit.folderName } : null)}
                            onChange={({ detail }) => setSelectedFolder({ label: detail.selectedOption.label ?? '', value: detail.selectedOption.value ?? '' })}
                            options={[
                                { label: "None", value: "" },
                                ...(videoToEdit ? existingVideoFolders : existingDocumentFolders).map(folder => ({
                                    label: folder,
                                    value: folder
                                })),
                                { label: "Create new folder", value: "CREATE_NEW" }
                            ]}
                            selectedAriaLabel="Selected"
                            placeholder={videoToEdit?.folderName || "Select a folder"}
                        />
                    </FormField>

                    {selectedFolder?.value === "CREATE_NEW" && (
                        <FormField label="New folder name">
                            <Input
                                value={newFolderName || ""}
                                onChange={({ detail }) => setNewFolderName(detail.value)}
                                placeholder="Enter new folder name"
                            />
                        </FormField>
                    )}

                    <Stack direction="horizontal" size="xs">
                        <Button
                            variant="primary"
                            onClick={() => {
                                const folderName = selectedFolder?.value === "CREATE_NEW"
                                    ? newFolderName
                                    : selectedFolder?.value;

                                if (videoToEdit) {
                                    handleEditVideo(videoToEdit, folderName);
                                }
                                if (documentToEdit) {
                                    handleEditDocument(documentToEdit, folderName)
                                }
                                setShowEditModal(false);
                                setVideoToEdit(null);
                                setDocumentToEdit(null);
                                setSelectedFolder(null);
                                setNewFolderName('');
                            }}
                            disabled={(selectedFolder?.value === "CREATE_NEW" && !newFolderName)}
                        >
                            Save
                        </Button>
                        <Button
                            onClick={() => {
                                setShowEditModal(false);
                                setVideoToEdit(null);
                                setDocumentToEdit(null);
                                setSelectedFolder(null);
                                setIsSafetyLesson(false)
                                setNewFolderName('');
                            }}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Stack>
            </Modal >
        </>
    )
}

